import React, { useState, useCallback, useEffect } from "react";
import styled from "styled-components";
import { loginAsync, selectLoadState, LANGUAGE_LIST } from "features/login";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "app/store";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logo from "assets/images/blue_logo-256652c5.png";
import logoCenter from "assets/images/Licence_portal_icon_blue__2_-removebg-preview.png";
import logoHelp from "assets/images/help_mark__1_-removebg-preview.png";

const LoginPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [origin, setOrigin] = useState(location.host.replace(/-user/, ""));
  const cookie =
    document.cookie
      .split("; ")
      .map((x) => x.split("="))
      .find((x) => x.length === 2 && x[0] === "language")?.[1] ?? "";
  const browserLang = window.navigator.language === "ja" ? "ja" : "en";
  const language = cookie === "" ? browserLang : cookie;

  const [countryState, SetCountry] = useState(language);
  const [t, i18n] = useTranslation();
  const onLogin = useCallback(() => {
    dispatch(loginAsync({ email, password }));
  }, [dispatch, email, password]);

  const load = useSelector(selectLoadState);
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const back = query.get("back");
  useEffect(() => {
    if (load === "Loaded") {
      if (back === "/optin") {
        navigate("/optin");
      } else {
        navigate("/software");
      }
    } else if (load === "LoadError") {
      setPassword("");
    }
  }, [navigate, load, countryState, back]);

  useEffect(() => {
    i18n.changeLanguage(countryState);
  }, [countryState, i18n]);
  const withLang = (url: string, lang: string) => {
    const finalLang = lang === "ja" ? "ja" : "en";
    return url.replace(/\/(ja|en)\//, `/${finalLang}/`);
  };
  return (
    <Wrapper>
      <LeftSection>
        <Header $isJapanese={countryState === "ja"}>
          <Logo src={logo} alt="OMRON" />
          <LanguageDl>
            <LanguageDt>{t("loginPage.label.displayLanguage")}：</LanguageDt>
            <LanguageDd>
              <LanguageSelect
                name=""
                onChange={(e) => {
                  SetCountry(e.currentTarget.value);
                  // 既にcookieに選択言語があれば削除
                  document.cookie = "language=; max-age=0";
                  // cookieの期限は1年
                  document.cookie = `language=${e.currentTarget.value}; max-age=31536000`;
                }}
                value={countryState}
              >
                {LANGUAGE_LIST.map((x) => (
                  <option key={x.value} value={x.value}>
                    {t(x.label)}
                  </option>
                ))}
              </LanguageSelect>
            </LanguageDd>
          </LanguageDl>
        </Header>
        <ContentWrapper>
          <LogoBody src={logoCenter} alt="OMRON" />
          <WelcomeTitle>{t("loginPage.label.titleBody")}</WelcomeTitle>
          <WelcomeMessage>{t("loginPage.label.titleLabelBody")}</WelcomeMessage>
          <FooterLink
            href={withLang(
              "https://www.fa.omron.co.jp/product/tool/ss-video-manual/ja/license/create-account_software-download/index.html",
              countryState
            )}
            target="_blank"
          >
            {t("loginPage.label.linkBody")}
          </FooterLink>
          <ContactInfo>{t("loginPage.label.labelFooter")}:</ContactInfo>
          <FooterLink href="mailto:CLIP-RECV-JP-J@omron.com">
            CLIP-RECV-JP-J@omron.com
          </FooterLink>
          {countryState === "ja" && (
            <MigrationNotice>
              <MigrationInfo>{t("loginPage.label.migrationNotice")}</MigrationInfo>
              <MigrationLink
                href={withLang(`https://${origin}/`, countryState)}
                target="_blank"
                rel="noopenner noreferrer"
              >
                {t("loginPage.label.migrationNoticeLink")}
              </MigrationLink>
            </MigrationNotice>
          )}
        </ContentWrapper>
      </LeftSection>
      <RightSection>
        <HeaderRight>
          <LogoHelpLink
            href={withLang(
              "https://www.fa.omron.co.jp/product/tool/ss-video-manual/ja/license/",
              countryState
            )}
            target="_blank"
          >
            <LogoHelp src={logoHelp} alt="OMRON" />
          </LogoHelpLink>
        </HeaderRight>
        <LoginBox>
          <LoginTitle>{t("loginPage.label.title")}</LoginTitle>
          <LoginDt>{t("loginPage.label.Id")}</LoginDt>
          <LoginInput
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <LoginDt>{t("loginPage.label.password")}</LoginDt>
          <LoginInput
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <LoginButton onClick={onLogin}>
            {t("loginPage.label.loginButton")}
          </LoginButton>
          <ForgetPasswordLink href="/reset">
            {t("loginPage.label.forgatPassword")}
          </ForgetPasswordLink>
          <Separator />
        </LoginBox>
        <LoginBox>
          <Text>{t("loginPage.text.newUser")}</Text>
          <p>
            <SignupLinkWrapper>
              <SignupLink
                onClick={() => {
                  document.cookie = "language=; max-age=0";
                  document.cookie = `language=${countryState}; max-age=31536000`;
                }}
                href="/signup"
              >
                {t("loginPage.label.sinupButton")}
              </SignupLink>
              <Popup className="popup">
                {t("loginPage.label.labelTooltipHeader")}
                <FooterLink
                  href={withLang(
                    "https://www.fa.omron.co.jp/product/tool/ss-video-manual/ja/license/create-account_software-download/index.html",
                    countryState
                  )}
                  target="_blank"
                >
                  {t("loginPage.label.labelTooltipBody")}
                </FooterLink>
                {t("loginPage.label.labelTooltipFooter")}
              </Popup>
            </SignupLinkWrapper>
            <Popup className="popup">{t("loginPage.tooltip.signupInfo")}</Popup>
          </p>
        </LoginBox>
        <LoginBox>
          <Text>{t("loginPage.text.privacyPolicy")}</Text>
          <LoginPolicyUl>
            <LoginPolicyLi>
              <LoginPolicyLink
                href="https://www.fa.omron.co.jp/footer/privacy.html"
                target="_blank"
              >
                {t("loginPage.label.japan")}
              </LoginPolicyLink>
            </LoginPolicyLi>
            <LoginPolicyLi>
              <LoginPolicyLink
                href="http://www.ia.omron.co.kr/member/police3.asp"
                target="_blank"
              >
                {t("loginPage.label.korea")}
              </LoginPolicyLink>
            </LoginPolicyLi>
            <LoginPolicyLi>
              <LoginPolicyLink
                href="https://www.fa.omron.com.cn/syxz"
                target="_blank"
              >
                {t("loginPage.label.china")}
              </LoginPolicyLink>
            </LoginPolicyLi>
            <LoginPolicyLi>
              <LoginPolicyLink
                href="https://www.omron-ap.com/privacy_policy.asp"
                target="_blank"
              >
                {t("loginPage.label.asiaPacific")}
              </LoginPolicyLink>
            </LoginPolicyLi>
            <LoginPolicyLi>
              <LoginPolicyLink
                href="https://industrial.omron.eu/en/misc/privacy-policy"
                target="_blank"
              >
                {t("loginPage.label.europeMiddleEastAfrica")}
              </LoginPolicyLink>
            </LoginPolicyLi>
            <LoginPolicyLi>
              <LoginPolicyLink
                href="https://automation.omron.com/en/us/privacy-policy"
                target="_blank"
              >
                {t("loginPage.label.unitedStates")}
              </LoginPolicyLink>
            </LoginPolicyLi>
          </LoginPolicyUl>
        </LoginBox>
      </RightSection>
    </Wrapper>
  );
};
export default LoginPage;

const Wrapper = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: "源ノ角ゴシック Regular", "源ノ角ゴシック", "Noto Sans JP",
    "Roboto", Helvetica, "ヒラギノ角ゴ Pro W3", "Hiragino Kaku Gothic Pro",
    "メイリオ", "Meiryo", "Meiryo UI", sans-serif;
  color: #333333;
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
`;
const LanguageDl = styled.dl`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 3rem;
`;
const LanguageDt = styled.dt`
  max-width: 35%;
  padding: 1.2rem 2.2%;
  font-size: 14px;
  line-height: 1.7;
  -webkit-letter-spacing: 0;
  -moz-letter-spacing: 0;
  -ms-letter-spacing: 0;
  letter-spacing: 0;
  font-weight: normal;
`;
const LanguageDd = styled.dd`
  width: 40%;
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
  text-align: left;
`;

const LeftSection = styled.div`
  width: 50%;
  background: #dcdcdc;
  padding: 2rem;
  align-items: center;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
`;

const RightSection = styled.div`
  width: 55%;
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  -webkit-flex-direction: column;
  -ms-flex-direction: column;
  flex-direction: column;
  -webkit-align-items: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  -ms-flex-pack: center;
  background: #ffffff;
`;
const ContentWrapper = styled.div`
  text-align: center;
  justify-content: flex-start;
  width: 100%;
`;
const Header = styled.div<{ $isJapanese?: boolean }>`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${(props) => (props.$isJapanese ? "2rem" : "8rem")};
`;
const HeaderRight = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  align-self: flex-end;
`;
const LoginDt = styled.dt`
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
  padding: 0.3rem 0;
  width: fit-content;
`;
const LogoBody = styled.img`
  height: 60px;
  object-fit: contain;
`;
const LogoHelp = styled.img`
  height: 35px;
  object-fit: contain;
`;
const LogoHelpLink = styled.a`
  height: 35px;
  object-fit: contain;
`;
const Logo = styled.img`
  height: 50px;
  object-fit: contain;
`;
const WelcomeTitle = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: #333;
  text-align: center;
  margin-bottom: 2rem;
`;
const WelcomeMessage = styled.div`
  justify-self: center;
  font-size: 1rem;
  color: #333;
  text-align: center;
  margin-bottom: 2rem;
  width: 50%;
`;

const LanguageSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  margin-top: 1rem;
`;

const ContactInfo = styled.div`
  margin-top: 2rem;
  font-size: 0.9rem;
  text-align: center;
  color: #666;
`;

const MigrationNotice = styled.div`
  width: 70%;
  margin: 2rem auto 0;
  padding: 1.2rem 1.5rem;
  text-align: center;
`;

const MigrationInfo = styled.div`
  font-size: 1.2rem;
  color: #d32f2f;
  text-align: center;
  font-weight: bold;
`;

const LoginBox = styled.div`
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const LoginTitle = styled.h2`
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 1.5rem;
`;

const LoginInput = styled.input`
  width: 94%;
  padding: 0.75rem;
  margin-bottom: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background-color: #005eb8;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  margin-bottom: 1rem;

  &:hover {
    background-color: #004b99;
  }
`;

const ForgetPasswordLink = styled.a`
  color: #005eb8;
  text-decoration: none;
  font-size: 0.9rem;

  &:hover {
    text-decoration: underline;
  }
`;

const Separator = styled.hr`
  border: 0;
  border-top: 1px solid #ddd;
  margin: 2rem 0;
`;

const NewRegistrationButton = styled.a`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background-color: #007bff;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    background-color: #0056b3;
  }
`;

const Footer = styled.div`
  margin-top: 2rem;
  font-size: 0.8rem;
  color: #666;
  text-align: center;
`;

const FooterLink = styled.a`
  color: #005eb8;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const MigrationLink = styled(FooterLink)`
  display: inline-block;
  margin-top: 1rem;
  font-weight: bold;
`;

const LoginPolicyUl = styled.ul`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`;
const LoginPolicyLi = styled.li`
  margin-right: 2rem;
  margin-bottom: 1rem;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  :last-child {
    margin-right: 0;
  }
`;
const LoginPolicyLink = styled.a`
  color: #005eb8;
`;
const ContactText = styled.p`
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
`;
const ContactLink = styled.a`
  color: #005eb8;
`;
const Text = styled.p`
  margin-bottom: 2rem;
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
  text-align: left;
`;
const SignupLinkWrapper = styled.div`
  position: relative;
  display: inline-block;

  &:hover .popup {
    display: block;
  }
`;
const SignupLink = styled.a`
  margin-bottom: 2rem;
  display: inline-block;
  width: 15rem;
  padding: 1.23rem 2rem;
  color: #005eb8;
  border: 1px #005eb8 solid;
  text-decoration: none;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  -moz-transition: all 0.3s ease-out;
  -ms-transition: all 0.3s ease-out;
  -webkit-transition: all 0.3s ease-out;
  transition: all 0.3s ease-out;
  :hover {
    background: rgba(0, 94, 184, 0.04);
  }
  :hover .popup {
    display: block;
  }
`;
const Popup = styled.div`
  display: none;
  position: absolute;
  transform: translateX(-50%);
  background-color: #fff;
  color: #333;
  text-align: center;
  border-radius: 5px;
  padding: 0.5rem 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;

  &:after {
    content: "";
    position: absolute;
    transform: translateX(-50%);
    border-width: 5px;
    border-style: solid;
    border-color: #fff transparent transparent transparent;
  }
`;
